import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { MessageService, TreeNode } from "primeng/api";
import { DialogService, DynamicDialogRef } from "primeng/dynamicdialog";
import { Observable, Subject, takeUntil } from "rxjs";
import {
  CollectionFilters,
  CollectionMultiSelectComponent,
} from "src/app/common/components/collections-multi-select/collections-multi-select.component";
import { FileInfo } from "src/app/common/components/file-content-list/file-content-list.component";
import { AssistantSettingsComponent } from "src/app/common/components/floating-assistant-chat/assistant-settings/assistant-settings.component";
import { AssistantData } from "src/app/common/models/assistant-data.model";
import { DragAndDropItem } from "src/app/common/models/drag-and-drop-item.model";
import { ChatCompletionMessageParam } from "src/app/common/models/llm-request.model";
import { AccountService } from "src/app/common/services/account.service";
import { ConfigService } from "src/app/common/services/config.service";
import { ErrorService } from "src/app/common/services/error.service";
import { FloatingAssistantChatService } from "src/app/common/services/floating-assistant-chat.service";
import { LlmConfigService } from "src/app/common/services/llm-config.service";
import { PythonApiMlService } from "src/app/common/services/python-api-ml.service";
import { SseEvent } from "src/app/common/services/xhr-source.service";
import { PromptTrackingService } from "src/app/modules/dashboard/services/prompt-tracking.service";
import { HomePageService } from "src/app/modules/home-page/services/home-page.service";
import { SavedChat } from "src/app/modules/prompts/models/backend-saved-chat.model";
import { PredefinedPrompt } from "src/app/modules/prompts/models/prompt-template.model";
import { PromptsService } from "src/app/modules/prompts/services/prompts.service";

// Event kinds from Python API */
interface SourcesEvent {
  kind: "sources";
  data: { title: string; uri: string }[];
}
interface SearchHtmlEvent {
  kind: "search_html";
  data: string;
}
interface FollowupsEvent {
  kind: "followups";
  data: string[];
}

interface SseImageEvent {
  kind: "image";
  data: {
    type: string;
    content: string;
  };
}

// Union of all SSE events we handle
type ExtendedSseEvent = SseEvent | SourcesEvent | SearchHtmlEvent | FollowupsEvent | SseImageEvent;

export interface SourceDto {
  title: string;
  uri: string;
}

export interface FollowUpDto {
  search: string;
  follow: string;
}

export type AssistantMessage = ChatCompletionMessageParam & {
  sources?: SourceDto[];
  followups?: FollowUpDto[];
};

@Component({
  templateUrl: "./assistant-chat.component.html",
  selector: "app-assistant-chat",
  styleUrls: ["./assistant-chat.component.scss"],
  providers: [MessageService],
})
export class AssistantChatComponent implements OnInit, OnDestroy {
  // Inject dependencies
  private readonly dialogService = inject(DialogService);
  private readonly messageService = inject(MessageService);
  private readonly errorService = inject(ErrorService);
  private readonly accountService = inject(AccountService);
  private readonly promptsService = inject(PromptsService);
  private readonly promptTrackingService = inject(PromptTrackingService);
  private readonly configService = inject(ConfigService);
  private readonly pythonApiMlService = inject(PythonApiMlService);
  protected readonly floatingAssistantChatService = inject(FloatingAssistantChatService);
  protected readonly homePageService = inject(HomePageService);
  protected readonly llmConfigService = inject(LlmConfigService);

  private defaultTemperature = 0.5;
  private defaultLlm = this.configService.defaultLlm;
  private lastEnableSearch = false;
  private currentAssistantIdx: number | null = null;

  private readonly DEFAULT_PROMPT = $localize`:@@assistant_chat_default_prompt:\
Vous êtes un modèle de langage avancé conçu pour assister les journalistes dans l'analyse d'articles \
et de transcriptions d'audios longues et complexes.
Votre rôle est de faciliter la compréhension du contenu, de répondre aux questions du journaliste \
de manière efficace et précise en fournissant des informations qui aideront le journaliste \
à construire son article ou à mener une analyse plus approfondie.

A chaque fois que l'information que vous fournissez provient de la transcription, par exemple lorsque \
vous répondez à une question sur la transcription ou que vous faites une citation, \
ajoutez toujours le timestamp de l'information fournie. Par exemple : "1:32: la référence au texte ou la citation"`;

  private readonly SYSTEM_PROMPT_FOR_HELP = `Vous aidez les journalistes \
à prendre en main une plateforme web. Pour chaque fonctionnalité, il y a une vidéo de démo et sa transcription. 
A chaque fois que l'information que vous fournissez provient de la transcription, \
ajoutez toujours l'id de la video concernée et le timestamp associé à l'information \
dans ce format: "video1 - 01:32: la référence..."
Ne cite pas plus d'une vidéo de démo par réponse pour éviter les confusions.
Voici les transcriptions:`;

  readonly showSearchToggleSubject = this.llmConfigService.hasSearchCapableModelSubject;
  readonly showImageCreationToggleSubject = this.llmConfigService.hasImageCapableModelSubject;

  @Input({ required: true })
  assistantData!: AssistantData;

  @Output() readonly historyToggled = new EventEmitter<void>();
  @Output() readonly uploadedFileClicked = new EventEmitter<void>();
  @Output() readonly followUp = new EventEmitter<{ prompt: string; enableSearch: boolean }>();

  settingsDialogRef: DynamicDialogRef | undefined;
  collectionSelectRef: DynamicDialogRef | undefined;

  // List of sources
  groundingSources: { title: string; uri: string }[] = []; // list of sources
  searchEntryHtml: string | null = null; // HTML content
  searchEntryHtmls: string[] = []; // list of HTML content
  followUpQueries: string[] = []; // list of follow-up queries
  queryPairs: { search: string; follow: string }[] = []; // list of query pairs

  imageCreationEnabled = false;

  @ViewChild("scrollableRef") scrollableRef!: ElementRef;
  private readonly destroySubject = new Subject<void>();

  async ngOnInit(): Promise<void> {
    // Initialize the assistant chat component
    const defaultLlmBackendKey = (await this.accountService.getLocalAccount()).config.defaultLlmModel;
    this.defaultLlm = this.llmConfigService.getTechnicalNameFromBackendKey(defaultLlmBackendKey);
    this.defaultTemperature = (await this.accountService.getLocalAccount()).config.defaultLlmTemperature;
    this.accountService.userConfigChange.pipe(takeUntil(this.destroySubject)).subscribe((config) => {
      this.defaultTemperature = config.defaultLlmTemperature;
      this.defaultLlm = this.llmConfigService.getTechnicalNameFromBackendKey(config.defaultLlmModel);
      this.assistantData.savedChat.model = this.defaultLlm;
      this.assistantData.savedChat.temperature = this.defaultTemperature;
    });
    if (this.assistantData.isFloating) {
      // Handle floating assistant
      this.floatingAssistantChatService.setMedia.pipe(takeUntil(this.destroySubject)).subscribe((media) => {
        this.setMedia(media);
      });
      this.floatingAssistantChatService.openAssistantForHelpEmitter
        .pipe(takeUntil(this.destroySubject))
        .subscribe(() => {
          void this.openAssistantForHelp();
        });

      // Handle the assistant response
      this.floatingAssistantChatService.openAssistantWithScrapedContentAndSummaryEmitter
        .pipe(takeUntil(this.destroySubject))
        .subscribe(({ scrapedContent, summary }) => {
          this.openAssistantWithScrapedContentAndSummary(scrapedContent, summary);
        });

      // Handle the assistant for RAG
      this.floatingAssistantChatService.openAssistantForRagEmitter
        .pipe(takeUntil(this.destroySubject))
        .subscribe((collectionFilters) => {
          this.openAssistantForRag(collectionFilters);
        });
    } else {
      // Handle the assistant history
      this.promptsService.restoreHistory.pipe(takeUntil(this.destroySubject)).subscribe((savedChat) => {
        void this.restoreHistory(savedChat);
      });
    }

    // Handle the prompt selection
    if (this.assistantData.selectedPrompt === undefined) {
      this.resetValuesToDefault();
    }

    this.initContextData();
  }

  ngOnDestroy(): void {
    // Clean up subscriptions
    this.settingsDialogRef?.close();
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  initContextData(): void {
    // Set the context of the assistant
    if (this.assistantData.initialContextData) {
      this.setMedia(this.assistantData.initialContextData);
    }
  }

  async saveChat(): Promise<void> {
    this.filterNonEmptyMessages();
    if (!this.assistantData.savedChat.messages.length) {
      return;
    }

    // Persist the object as-is
    const chatToSave: SavedChat = { ...this.assistantData.savedChat };
    await this.promptsService.saveChat(chatToSave);

    if (this.assistantData.savedChat.promptId) {
      if (this.assistantData.savedChat.promptId !== this.assistantData.lastPromptId) {
        this.assistantData.lastPromptId = this.assistantData.savedChat.promptId;
        await this.promptTrackingService.trackPrompt(this.assistantData.lastPromptId);
      }
    }
  }

  filterNonEmptyMessages(): void {
    // Filter out empty messages
    this.assistantData.savedChat.messages = this.assistantData.savedChat.messages
      .map((msg) => ({ ...msg, content: String(msg.content).trim() }))
      .filter((msg) => msg.content.length > 0 || msg.files?.length);
  }

  onPromptSelected(selectedPrompt: TreeNode<PredefinedPrompt>): void {
    // Store the node in component state first
    this.assistantData.selectedPrompt = selectedPrompt;

    // If there is no `data` payload, abort
    if (!selectedPrompt.data) {
      return;
    }

    const data = selectedPrompt.data;
    this.assistantData.contextFiles = data.contextFiles || [];
    this.setContext(data.systemPrompt);
    this.assistantData.savedChat.temperature = data.temperature;
    this.assistantData.savedChat.model = data.model;
    this.assistantData.savedChat.promptId = data.id;
    this.assistantData.userInput = data.userPrompt;
    this.assistantData.lastPromptId = null;
    this.assistantData.searchGrounding = Boolean(data.searchGrounding);
  }

  setContext(context: string): void {
    // Set the context of the assistant
    this.assistantData.savedChat.messages = this.assistantData.savedChat.messages.filter(
      (msg) => msg.role !== "system",
    );
    this.assistantData.savedChat.messages.splice(0, 0, {
      role: "system",
      content: context,
      files: this.assistantData.contextFiles,
    });
  }

  async reset(): Promise<void> {
    // Reset the chat
    await this.stopGenerating();
    this.messageService.clear();
    await this.saveChat();
    this.groundingSources = [];
    this.searchEntryHtml = null;
    this.searchEntryHtmls = [];
    this.followUpQueries = [];
    this.queryPairs = [];
    this.resetValuesToDefault();
  }

  onUserInputChanged(userInput: string): void {
    // Update the user input
    this.assistantData.userInput = userInput;
  }

  resetValuesToDefault(): void {
    // Reset the chat
    this.assistantData.selectedPrompt = undefined;
    this.assistantData.savedChat = {
      id: crypto.randomUUID(),
      messages: [],
      createdAt: new Date().toISOString(),
      temperature: this.defaultTemperature,
      model: this.defaultLlm,
      promptId: undefined,
    };
    this.assistantData.userInput = "";
    if (this.assistantData.isFloating) {
      this.floatingAssistantChatService.draggedItem = null;
      this.floatingAssistantChatService.selectedMediaItems = [];
      this.floatingAssistantChatService.selectionMode = false;
    }
    this.assistantData.summaryReport = "";
    this.assistantData.contextFiles = [];
    this.assistantData.collections = [];
    this.assistantData.lastPromptId = null;
  }

  pushMessage(message: ChatCompletionMessageParam): void {
    // Add the message to the chat
    this.assistantData.savedChat.messages.push(message);
    this.scrollDown();
  }

  async restoreHistory(saved: SavedChat): Promise<void> {
    await this.reset();
    this.assistantData.savedChat = saved;

    const allSources: SourceDto[] = [];
    const allFollowups: FollowUpDto[] = [];

    saved.messages.forEach((m) => {
      const assistantMsg = m as AssistantMessage;
      allSources.push(...(assistantMsg.sources ?? []));
      allFollowups.push(...(assistantMsg.followups ?? []));
    });

    // De-duplicate sources by URI (case-insensitive)
    this.groundingSources = Array.from(new Map(allSources.map((s) => [s.uri.trim().toLowerCase(), s])).values());

    // De-duplicate follow-ups by search|follow pair
    const seen = new Set<string>();
    const uniqFollowups = allFollowups.filter((f) => {
      const key = `${f.search.trim()}|${f.follow.trim().toLowerCase()}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });

    this.queryPairs = uniqFollowups.map(({ search, follow }) => ({ search, follow }));
    this.followUpQueries = uniqFollowups.map((f) => f.search);
  }

  addToLastMessage(text: string): void {
    // Add text to the last message
    if (this.assistantData.savedChat.messages.length > 0) {
      this.assistantData.savedChat.messages.at(-1)!.content += text;
    }
    this.scrollDown();
  }

  deleteMessage(index: number): void {
    // Remove the message at the given index
    this.assistantData.savedChat.messages.splice(index, 1);
  }

  async reloadFirstUserMessage(): Promise<void> {
    // Reload the first user message
    if (!this.assistantData.isFloating) {
      return;
    }
    // Check if there are selected media items
    if (this.floatingAssistantChatService.selectedMediaItems.length === 0) {
      return;
    }
    // Check if the first user message is empty
    const copyOfMediaItems = [...this.floatingAssistantChatService.selectedMediaItems];
    await this.reset();
    this.floatingAssistantChatService.selectedMediaItems = copyOfMediaItems;
    this.setContext(this.DEFAULT_PROMPT);
    this.pushMessage({
      role: "assistant",
      content: $localize`:@@assistant_chat_copy_media_reload_prompt:\
Contenu **${copyOfMediaItems[0]!.title}** chargé. Comment puis-je vous aider ?`,
    });
  }

  getContext(): string {
    // Get the context of the assistant
    return String(this.assistantData.savedChat.messages.find((msg) => msg.role === "system")?.content ?? "");
  }

  setMedia(media: DragAndDropItem): void {
    // Set the media for the assistant
    this.resetValuesToDefault();
    this.setContext(this.DEFAULT_PROMPT);
    this.pushMessage({
      role: "assistant",
      content: $localize`:@@assistant_chat_copy_media_prompt:\
Contenu **${media.title}** chargé. Comment puis-je vous aider ?`,
    });
  }

  async openAssistantForHelp(): Promise<void> {
    // Open the assistant for help
    this.resetValuesToDefault();
    const features = await this.homePageService.getFeatures();
    const instructionsConcatenated = features
      .map((feature, index) => {
        return JSON.stringify({ videoId: `video${index}`, transcript: feature.transcript });
      })
      .join("========================================\n\n");
    this.setContext(`${this.SYSTEM_PROMPT_FOR_HELP}\n\n${instructionsConcatenated}`);
    this.pushMessage({
      role: "assistant",
      content: $localize`:@@assistant_chat_baker_street_help_prompt:\
Comment puis-je vous aider sur l'utilisation et les fonctionnalités de Bakerstreet ?`,
    });
  }

  openAssistantWithScrapedContentAndSummary(scrapedContent: string, summary: string): void {
    // Open the assistant with scraped content and summary
    if (!this.assistantData.isFloating) {
      return;
    }
    this.resetValuesToDefault();
    this.assistantData.savedChat.model = this.configService.largestLlm;
    this.assistantData.summaryReport = summary;
    this.pushMessage({
      role: "user",
      content: scrapedContent,
      hidden: true,
    });
    this.pushMessage({
      role: "assistant",
      content: summary,
      hidden: true,
    });
    this.pushMessage({
      role: "assistant",
      content: $localize`:@@assistant_chat_open_with_scraped_content_prompt:\
Contenu du scraping chargé. Le rapport d'analyse est affiché ci-dessus. Comment puis-je vous aider ?`,
    });
    this.scrollDown();
    void this.saveChat().then(() => {
      this.floatingAssistantChatService.newChatCreated.emit(this.assistantData.savedChat.id);
    });
  }

  openSettings(): void {
    // Open the assistant settings dialog
    this.settingsDialogRef = this.dialogService.open(AssistantSettingsComponent, {
      header: $localize`:@@assistant_chat_parameter_header:Paramètres de la discussion`,
      width: "500px",
      resizable: true,
      draggable: true,
      data: {
        temperature: this.assistantData.savedChat.temperature,
        model: this.assistantData.savedChat.model,
        context: this.getContext(),
        contextFiles: this.assistantData.contextFiles,
        searchGrounding: this.assistantData.searchGrounding,
      },
    });
    this.settingsDialogRef.onClose.pipe(takeUntil(this.destroySubject)).subscribe((data) => {
      if (data?.save) {
        this.assistantData.savedChat.temperature = data.temperature;
        this.assistantData.savedChat.model = data.model;
        this.assistantData.contextFiles = data.contextFiles;
        this.setContext(data.context);
        this.assistantData.searchGrounding = Boolean(data.searchGrounding);
      }
    });
  }

  onDeleteMessage(index: number): void {
    // Delete the message at the given index
    this.deleteMessage(index);
    this.messageService.clear();
  }

  removeCollection(id: string): void {
    // Remove the collection with the given ID
    this.assistantData.collections = this.assistantData.collections.filter((collection) => collection.id !== id);
  }

  openDialogToSelectCollections(): void {
    // Open the dialog to select collections
    this.collectionSelectRef = this.dialogService.open(CollectionMultiSelectComponent, {
      header: $localize`:@@chat_input_collection_tooltip:Ajouter des émissions à rechercher (Expérimental)`,
      data: {
        collections: this.assistantData.collections,
        period: [
          this.assistantData.collectionDateFrom ? new Date(this.assistantData.collectionDateFrom) : null,
          this.assistantData.collectionDateTo ? new Date(this.assistantData.collectionDateTo) : null,
        ],
      },
    });
    // Subscribe to the dialog close event
    this.collectionSelectRef.onClose
      .pipe(takeUntil(this.destroySubject))
      .subscribe((collectionFilters: CollectionFilters) => {
        if (collectionFilters) {
          this.onCollectionsSelected(collectionFilters);
        }
      });
  }

  openAssistantForRag(collectionFilters: CollectionFilters): void {
    // Open the assistant for RAG
    this.resetValuesToDefault();
    this.onCollectionsSelected(collectionFilters);
  }

  onCollectionsSelected(collectionFilters: CollectionFilters): void {
    // Handle the selected collections
    const selectedCollectionsIds = new Set(collectionFilters.collections.map((c) => c.id));
    this.assistantData.collections = this.assistantData.collections.filter((c) => !selectedCollectionsIds.has(c.id));
    this.assistantData.collections = this.assistantData.collections.concat(collectionFilters.collections);
    this.assistantData.collectionDateFrom = collectionFilters.period?.[0]?.toISOString();
    this.assistantData.collectionDateTo = collectionFilters.period?.[1]?.toISOString();
  }

  scrollDown(): void {
    // Scroll down the chat
    if (this.assistantData.disableScrolling) {
      return;
    }
    setTimeout(() => {
      this.scrollableRef.nativeElement.scrollTop = this.scrollableRef.nativeElement.scrollHeight;
    }, 0);
  }

  onScroll(): void {
    // Handle the scroll event
    const scrollingUp = this.assistantData.currentScrollPosition
      ? this.scrollableRef.nativeElement.scrollTop < this.assistantData.currentScrollPosition
      : false;
    if (scrollingUp) {
      this.assistantData.disableScrolling = true;
    }
    const isAtBottom =
      Math.abs(
        this.scrollableRef.nativeElement.scrollHeight
          - this.scrollableRef.nativeElement.scrollTop
          - this.scrollableRef.nativeElement.clientHeight,
      ) < 10;
    if (isAtBottom) {
      this.assistantData.disableScrolling = false;
    }
    this.assistantData.currentScrollPosition = this.scrollableRef.nativeElement.scrollTop;
  }

  async stopGenerating(): Promise<void> {
    // Stop the generation of the assistant
    this.assistantData.streamSubscribtion?.unsubscribe();
    this.assistantData.genPending = false;
    await this.saveChat();
  }

  onSend(event: {
    // Handle the send event
    prompt: string;
    files?: FileInfo[];
    content?: DragAndDropItem;
    enableSearch: boolean;
    enableImageCreation: boolean;
  }): void {
    this.assistantData.genPending = true;
    this.assistantData.disableScrolling = false;
    this.lastEnableSearch = event.enableSearch;
    this.imageCreationEnabled = event.enableImageCreation;
    this.pushMessage({ role: "user", content: event.prompt, files: event.files });

    // Add the selected media items to the chat
    if (this.assistantData.isFloating && this.floatingAssistantChatService.selectedMediaItems.length > 0) {
      const concatenatedText = this.floatingAssistantChatService.selectedMediaItems
        .map((item, index) => $localize`:@@assistant_chat_selected_media_list:**Article: ${index}**: ${item.text}`)
        .join("\n=====================\n");
      this.pushMessage({
        role: "user",
        content: $localize`:@@assistant_chat_selected_media_text:voici les textes des médias:\n${concatenatedText}\n`,
      });
    }

    // Add the selected media item to the chat
    if (event.content) {
      this.pushMessage({
        role: "user",
        content: $localize`:@@assistant_chat_selected_media_text_with_context:voici le texte du média:\n${event.content.text}\n`,
      });
    }

    // Add the initial context data to the chat
    if (!this.assistantData.isFloating && this.assistantData.initialContextData) {
      this.pushMessage({
        role: "user",
        content: $localize`:@@assistant_chat_selected_media_text_with_context:voici le texte du média:\n${this.assistantData.initialContextData.text}\n`,
      });
    }

    // Add the assistant message to the chat
    this.pushMessage({ role: "assistant", content: "" });
    this.currentAssistantIdx = this.assistantData.savedChat.messages.length - 1; // NEW

    this.generateResponse();
  }

  setError(err: unknown): void {
    // Handle the error
    const error = err as { message?: string; stack?: string } | undefined;
    if (error) {
      this.errorService.sendError(error.stack || error.message || JSON.stringify(err));
    }
    const detail =
      err instanceof MessageEvent
        ? err.data
        : $localize`:@@assistant_chat_error_retry_message:${error?.message || String(err)} Veuillez ré-essayer`;
    this.messageService.add({
      severity: "error",
      summary: "Erreur",
      detail,
    });
    this.assistantData.genPending = false;
    this.filterNonEmptyMessages();
    this.scrollDown();
  }

  generateResponse(): void {
    // Determine model to use for the response based on toggles
    let modelToUse: string;
    if (this.lastEnableSearch) {
      // If the prompt itself had searchGrounding=true, use its own model:
      if (this.assistantData.searchGrounding) {
        modelToUse = this.assistantData.savedChat.model;
      } else {
        // Otherwise (user manually toggled search), fall back to default:
        modelToUse = this.configService.searchLlm;
      }
    } else if (this.imageCreationEnabled && this.configService.imageLlm) {
      modelToUse = this.configService.imageLlm;
    } else {
      modelToUse = this.assistantData.savedChat.model;
    }

    // Generate the response from the assistant
    this.groundingSources = [];
    this.searchEntryHtml = null;
    this.searchEntryHtmls = [];
    this.followUpQueries = [];
    this.queryPairs = [];

    // Filter out empty messages
    const filteredMessages = this.assistantData.savedChat.messages.filter((msg) => msg.content || msg.files?.length);

    // Check if there are no messages
    let stream: Observable<SseEvent>;
    try {
      // Create the stream for the LLM response
      stream = this.pythonApiMlService.streamLLMResponse({
        messages: filteredMessages,
        model: modelToUse,
        temperature: this.assistantData.savedChat.temperature,
        collections: this.assistantData.collections?.map((c) => c.id),
        collection_date_from: this.assistantData.collectionDateFrom,
        collection_date_to: this.assistantData.collectionDateTo,
        enable_search: this.lastEnableSearch,
        enable_image_creation: this.imageCreationEnabled,
      });
    } catch (err: unknown) {
      this.setError(err);
      return;
    }

    // Subscribe to the stream and handle the events
    let firstValue = true;
    let currentSources: SourceDto[] = [];
    const currentHrefBuf: string[] = [];
    let currentQueryPairs: FollowUpDto[] = [];

    // Subscribe to the stream
    this.assistantData.streamSubscribtion = stream.pipe(takeUntil(this.destroySubject)).subscribe({
      // Handle the events
      next: (evt: ExtendedSseEvent) => {
        switch (evt.kind) {
          case "text": {
            // Handle the text event
            const text = evt.data;
            if (firstValue && this.assistantData.collections.length) {
              firstValue = false;
              this.assistantData.savedChat.messages.splice(-1, 0, {
                role: "user",
                content: text,
                hidden: true,
              });
              if (this.currentAssistantIdx !== null) {
                this.currentAssistantIdx += 1;
              }
            } else {
              this.addToLastMessage(text);
            }
            break;
          }
          case "sources": {
            // Handle the sources event
            currentSources = evt.data;
            this.groundingSources = evt.data;
            break;
          }
          case "search_html": {
            // Handle the search HTML event
            const html = evt.data;
            currentHrefBuf.push(...Array.from(html.matchAll(/<a[^>]+href="([^"]+)"/gi), (m) => m[1]!));
            const urls = Array.from(html.matchAll(/<a[^>]+href="([^"]+)"/gi), (m) => m[1]!);
            this.searchEntryHtmls.push(...urls);
            break;
          }
          case "followups": {
            // Handle the follow-ups event
            this.followUpQueries = evt.data;
            this.queryPairs = evt.data.map((q: string, i: number) => ({
              search: this.searchEntryHtmls[i] ?? "",
              follow: q,
            }));

            // Update the follow-up queries
            const localPairs: FollowUpDto[] = evt.data.map((q: string, i: number) => ({
              search: currentHrefBuf[i] ?? "",
              follow: q,
            }));
            currentQueryPairs = localPairs;

            // Store to assistant message immediately so UI can pick it
            if (this.currentAssistantIdx != null) {
              const msg = this.assistantData.savedChat.messages[this.currentAssistantIdx] as AssistantMessage;
              msg.followups = localPairs;
            }
            break;
          }
          case "image": {
            const imageData = evt.data as { type: string; index?: number; content: string };
            if (imageData.type === "partial") {
              // Handle partial image
              const markdown = `![Generated Image](data:image/png;base64,${imageData.content})`;
              // Add to a temporary message or update existing preview
              this.updateImagePreview(markdown, imageData.index ?? 0);
            }
            break;
          }
          default:
            break;
        }
      },
      error: (err) => {
        // Handle the error
        if (err instanceof Error && err.message === "Request was aborted.") {
          this.assistantData.genPending = false;
          return;
        }
        this.setError(err);
      },
      complete: () => {
        // Find the last visible assistant answer
        const assistantMessages = this.assistantData.savedChat.messages
          .map((m, i) => ({ m, i }))
          .filter(({ m }) => m.role === "assistant" && !(m as AssistantMessage).hidden);

        const lastAssistant = assistantMessages.at(-1);
        if (lastAssistant) {
          const msg = lastAssistant.m as AssistantMessage;
          msg.sources ??= currentSources;
          msg.followups ??= currentQueryPairs;
          this.currentAssistantIdx = lastAssistant.i;
        }

        // Persist the conversation (includes messageMetas)
        void this.saveChat();

        this.assistantData.genPending = false;
        this.currentAssistantIdx = null;
      },
    });
  }

  private updateImagePreview(markdown: string, index: number): void {
    // Get the last assistant message
    const lastAssistantMsg = this.assistantData.savedChat.messages.filter((m) => m.role === "assistant").pop();

    if (lastAssistantMsg) {
      // Update the content of the last message
      lastAssistantMsg.content = markdown;
    } else {
      // If no assistant message exists yet, create one
      this.assistantData.savedChat.messages.push({
        role: "assistant",
        content: markdown,
      });
    }
  }

  // Check if the message is an assistant message
  private isAssistantMessage(msg: ChatCompletionMessageParam): msg is AssistantMessage {
    return Boolean((msg as Partial<AssistantMessage>).sources) || Boolean((msg as Partial<AssistantMessage>).followups);
  }

  // Check if the message has sources
  hasSources(msg: ChatCompletionMessageParam): boolean {
    return this.isAssistantMessage(msg) && Boolean(msg.sources?.length);
  }

  // Get the sources from the message
  getSources(msg: ChatCompletionMessageParam): SourceDto[] {
    return this.isAssistantMessage(msg) ? (msg.sources ?? []) : [];
  }

  // Check if the message has follow-ups
  hasFollowups(msg: ChatCompletionMessageParam): boolean {
    return this.isAssistantMessage(msg) && Boolean(msg.followups?.length);
  }

  // Get the follow-ups from the message
  getFollowups(msg: ChatCompletionMessageParam): FollowUpDto[] {
    return this.isAssistantMessage(msg) ? (msg.followups ?? []) : [];
  }

  // Track the source by its URI
  trackByUri(index: number, source: { uri: string }): string {
    return source.uri;
  }

  // Track the follow-up pair by its search and follow values
  trackByFollow(index: number, pair: { search: string; follow: string }): string {
    return `${pair.search}|${pair.follow}`;
  }

  // Toggle the multi-selection mode
  toggleMultiSelection(): void {
    if (!this.assistantData.isFloating) {
      return;
    }
    if (this.floatingAssistantChatService.selectionMode) {
      this.floatingAssistantChatService.selectionMode = false;
    } else {
      this.floatingAssistantChatService.selectionMode = true;
      this.floatingAssistantChatService.setPredefinedPromptByLabel.emit(
        "RTSinfo - Résumé Whatsapp d'articles - titres & User needs",
      );
    }
  }
}
